import { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { css, Theme, useTheme } from '@emotion/react';
import { Hexagon, IdentificationBadge, Gear, SignOut, CurrencyEur } from '@phosphor-icons/react';
import { AccessRights } from 'common/account/AccessRights';
import { useAccount, useUser } from 'client/lib/User';
import { useUserState } from 'client/state/UserState';
import { Button } from 'client/components/Button';
import { AccountSwitcher } from 'client/components/Other/AccountSwitcher';
import { Logo } from 'client/components/Other/Logo';
import { OverflowMenu, OverflowMenuItemProps } from 'client/components/OverflowMenu';
import { Pipeline } from 'client/components/Postcard/Pipeline';
import { layoutStyles, navbarHeight } from 'client/Theme';

export interface NavigationProps {
  fixed?: boolean;
}

export const Navigation = ({ fixed }: NavigationProps) => {
  const account = useAccount();
  const { user, hasAccessRight, logout } = useUser();
  const theme = useTheme();
  const isAdmin = hasAccessRight(AccessRights.ADMIN_PAGES);

  const isSingleAccount = (user?.accounts || []).length <= 1;

  useEffect(() => {
    if (account?.identifier) useUserState.setState({ account: account.identifier });
  }, [account?.identifier]);

  const creditsItem = useMemo<OverflowMenuItemProps>(
    () => ({
      id: 'credits',
      title: (account?.freeCreditLeft || 0).toFixed(2),
      icon: <CurrencyEur weight="bold" />,
      disabled: true,
      hidden: !account,
      visibleElement: ({ title, icon }) => (
        <Button key="credits" icon={icon} tooltip="Free Credits" tooltipSide="left" disabled css={creditsStyle}>
          <Pipeline color={theme.color.primary[60]} css={pipelineStyle} />
          {title}
        </Button>
      ),
    }),
    [account]
  );

  const projectsItem = useMemo<OverflowMenuItemProps>(
    () => ({
      id: 'projects',
      title: 'Projects',
      icon: <Hexagon />,
      href: account ? `/${account.identifier}/projects` : undefined,
      disabled: !account,
      visibleElement: ({ title, icon, href, disabled }) => (
        <Button key="projects" href={href} icon={icon} disabled={disabled}>
          {title}
        </Button>
      ),
    }),
    [account]
  );

  const adminItem = useMemo<OverflowMenuItemProps>(
    () => ({
      id: 'admin',
      title: 'Admin',
      icon: <IdentificationBadge />,
      href: '/admin',
      hidden: !isAdmin,
      visibleElement: ({ title, icon, href }) => (
        <Button key="admin" href={href} icon={icon}>
          {title}
        </Button>
      ),
    }),
    [isAdmin]
  );

  const settingsItem = useMemo<OverflowMenuItemProps>(
    () => ({
      id: 'settings',
      title: 'Settings',
      icon: <Gear />,
      href: '/settings',
      visibleElement: ({ title, icon, href }) => (
        <Button key="settings" href={href} icon={icon}>
          {title}
        </Button>
      ),
    }),
    []
  );

  const logoutItem = useMemo<OverflowMenuItemProps>(
    () => ({
      id: 'logout',
      title: 'Log Out',
      icon: <SignOut />,
      onClick: logout,
      visibleElement: ({ icon, onClick }) => <Button key="logout" icon={icon} onClick={onClick} />,
    }),
    []
  );

  const menuItems = [creditsItem, projectsItem, adminItem, settingsItem, logoutItem];
  const overflowMenuItems = useMemo(() => menuItems, menuItems);

  return (
    <header css={[headerStyle, fixed && fixedHeaderStyle]}>
      <div css={containerStyle}>
        <div css={switchStyle}>
          <Link to="/">
            <Logo width={108} height={32} />
          </Link>
          {!isSingleAccount && (
            <>
              <hr />
              <AccountSwitcher />
            </>
          )}
        </div>

        <div css={actionsStyle}>
          <OverflowMenu items={overflowMenuItems} />
        </div>
      </div>
    </header>
  );
};

const creditsStyle = (theme: Theme) => css`
  height: 46px;
  border-radius: 64px;
  border: 2px solid ${theme.color.primary[100]};
  background-color: transparent !important;
  cursor: default !important;
  overflow: hidden;

  > span {
    color: ${theme.color.foreground} !important;
  }

  > span:not(.icon) {
    transform: scale(1.2);
  }
`;

const pipelineStyle = css`
  position: absolute;
  width: 300px;
  transform: translate(-125px, -50px) scaleX(-1);
  z-index: -1;
`;

const headerStyle = (theme: Theme) => css`
  position: relative;
  height: ${navbarHeight}px;
  width: 100%;
  background-color: ${theme.color.background};

  img {
    height: 32px;
  }

  &:after {
    content: '';
    display: block;
    position: absolute;
    left: 0;
    bottom: 0;
    height: 2px;
    width: 100%;
    background-color: ${theme.color.grey[60]};
  }
`;

const fixedHeaderStyle = css`
  position: absolute;
`;

const containerStyle = css`
  ${layoutStyles}
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0;
  height: 100%;
`;

const actionsStyle = css`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const switchStyle = (theme: Theme) => css`
  display: flex;
  align-items: center;

  > a {
    display: flex;
  }

  > hr {
    display: block;
    margin: 0 12px 0 22px;
    height: 32px;
    width: 2px;
    border: none;
    background-color: ${theme.color.grey[60]};
  }
`;
